import React, { useRef, useLayoutEffect, useState } from 'react';
import { FaExpandAlt } from 'react-icons/fa';
import XModal from './XModal';
import { cn } from '../../utils/classNames';

const labelClasses = 'block mb-2 text-content font-sans';

const textareaClasses = `
  block w-full h-40 px-3 py-1.5
  font-sans text-base text-content
  bg-white border border-border rounded-md
  resize-none transition-colors
  focus:outline-none focus:ring-2 focus:ring-primary-ring
  disabled:opacity-60 disabled:cursor-not-allowed
`;

const errorClasses = 'block mt-1 ml-1 text-sm text-danger';

// Grows a textarea to fit its content.
const fitToContent = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
};

const XTextarea = ({ label, name, value, onChange, placeholder, disabled = false, className, autoResize = false, error, expandable = false, expandTitle, ...props }) => {
    const textareaRef = useRef(null);
    const expandedTextareaRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);

    useLayoutEffect(() => {
        if (autoResize) fitToContent(textareaRef.current);
    }, [autoResize, value]);

    // The expanded view always fits its content, re-measured when it opens and as the text changes.
    useLayoutEffect(() => {
        if (isExpanded) fitToContent(expandedTextareaRef.current);
    }, [isExpanded, value]);

    return (
        <React.Fragment>
            {label && <label className={labelClasses}>{label}</label>}
            <div className={expandable ? 'relative' : undefined}>
                <textarea
                    ref={textareaRef}
                    name={name}
                    value={value || ''}
                    onChange={onChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={cn(textareaClasses, autoResize && 'resize-none overflow-hidden', expandable && 'pr-9', className)}
                    {...props}
                />
                {expandable && (
                    <button
                        type="button"
                        onClick={() => setIsExpanded(true)}
                        className="absolute right-2 top-2 text-body hover:text-content cursor-pointer"
                        aria-label="Expand"
                        title="Expand"
                    >
                        <FaExpandAlt size={14} aria-hidden="true" />
                    </button>
                )}
            </div>
            {expandable && (
                <XModal
                    isOpen={isExpanded}
                    title={expandTitle || label || 'Edit'}
                    onClose={() => setIsExpanded(false)}
                    onSubmit={() => setIsExpanded(false)}
                    submitText="Done"
                    size="lg"
                >
                    <textarea
                        ref={expandedTextareaRef}
                        value={value || ''}
                        onChange={onChange}
                        placeholder={placeholder}
                        disabled={disabled}
                        className={cn(textareaClasses, 'h-auto min-h-75 overflow-hidden')}
                    />
                </XModal>
            )}
            {error && <span className={errorClasses}>{error}</span>}
        </React.Fragment>
    );
};

export default XTextarea;
